import {Component, inject, signal} from '@angular/core';
import {LoggerService} from '../shared/services/logger.service';
import {MenuItem, PrimeIcons} from 'primeng/api';
import {selectUserId} from '../auth/store/auth.selectors';
import {AuthOptions} from '../shared/models/auth-options.model';
import {PermissionService} from '../shared/services/permission.service';
import {AbstractCoreComponent} from '../shared/abstracts/abstract-core.component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {EnAuthOptionsOperator} from "../shared/enums/auth-options-operator.enum";
import {PRACTITIONER, SUPER_ADMIN} from '../shared/constants/auth';

@Component({
  selector: 'app-sidebar-menu',
  providers: [],
  template: `
    @if (loading()) {
      <div class="flex flex-row justify-center items-center -ml-5">
        <p-progress-spinner i18n-ariaLabel ariaLabel="Caricamento"
                            strokeWidth="5"
                            fill="transparent"
                            animationDuration=".5s"
                            [style]="{ width: '25px', height: '25px' }" />
      </div>
    } @else {
      <ul class="layout-menu">
        @for (item of menu(); track i; let i = $index) {
          @if (!item.separator) { <li app-menu-item [item]="item" [index]="i" [root]="true"></li> }
        }
      </ul>
    }
  `,
  standalone: false,
})
export class AppSidebarMenuComponent extends AbstractCoreComponent {
  protected override readonly logger = inject(LoggerService);
  private readonly permissionService = inject(PermissionService);

  loading = signal(false);

  menu = signal<MenuItem[]>([]);

  userId: number | null = null;

  private _userId = this.store.select(selectUserId);

  constructor() {
    super();
    this.loading.set(true);
    this._userId.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((id: number | null) => this.userId = id);

    // Rebuild menu once permissions are stable (after login)
    this.store.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const separator = { separator: true } as MenuItem;
      this.menu.set([
        {
          label: $localize`K•HERO`,
          items: [
            {
              label: $localize`Dashboard`,
              icon: PrimeIcons.OBJECTS_COLUMN,
              routerLink: ['/app/dashboard'],
            },
          ],
        },
        separator,
        {
          label: $localize`Amministrazione`,
          items: [
            {
              // `roles` narrows on TOP of `auth`: the permission says whether
              // this account may read clients, the role says whether managing them
              // belongs to its job at all. Both must pass — see docs/PERMISSIONS.md
              // for why a role alone is not a safe gate.
              label: $localize`Clienti`,
              icon: PrimeIcons.BUILDING,
              routerLink: ['/app/clients'],
              auth: this.createAuthOptions(['CLIENT_READ']),
            },
            {
              label: $localize`Utenti`,
              icon: PrimeIcons.USERS,
              routerLink: ['/app/users'],
              auth: this.createAuthOptions(['USER_READ']),
            },
            {
              label: $localize`Pazienti`,
              icon: PrimeIcons.USERS,
              routerLink: ['/app/members'],
              auth: this.createExactAuthOptions(['USER_RELATION_READ']) // will hide for the superadmin this item
            },
            {
              label: $localize`Dispositivi`,
              icon: PrimeIcons.MOBILE,
              routerLink: ['/app/stations'],
              auth: this.createAuthOptions(['STATION_READ']),
            },
            {
              label: $localize`Terapie`,
              icon: PrimeIcons.BRIEFCASE,
              routerLink: ['/app/programs'],
              auth: this.createAuthOptions(['PROGRAM_READ']),
              roles: this.createAuthOptions([PRACTITIONER]),
            },
            {
              label: $localize`Sessioni`,
              icon: PrimeIcons.SITEMAP,
              routerLink: ['/app/sessions'],
              auth: this.createExactAuthOptions(['SESSION_READ']),
              roles: this.createAuthOptions([PRACTITIONER]),
            },
            {
              label: $localize`Esercizi`,
              icon: PrimeIcons.LIST,
              routerLink: ['/app/exercises'],
              auth: this.createAuthOptions(['EXERCISE_READ', 'EXERCISE_READ_ONE', 'EXERCISE_UPDATE', 'EXERCISE_CREATE','EXERCISE_RESTORE', 'EXERCISE_DELETE'], EnAuthOptionsOperator.AND),
            },
            {
              // Gated on the WRITE permissions, not TAG_READ: every role reads
              // tags (the exercise filters are built from them), only a
              // super-admin edits the vocabulary.
              label: $localize`Tag`,
              icon: PrimeIcons.TAGS,
              routerLink: ['/app/tags'],
              auth: this.createAuthOptions(['TAG_CREATE', 'TAG_UPDATE', 'TAG_DELETE'], EnAuthOptionsOperator.AND),
            },
          ],
        },
      ]);
      this.loading.set(false);
    });
  }

  protected getItemAuth(item: MenuItem): AuthOptions | null {
    return item['auth'] || null;
  }
}
